import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, Ban, Download, FileSpreadsheet, FileText, Stamp,
  Trash2,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

/**
 * A single document: what it says, and the two irreversible things you can do
 * to it.
 *
 * Issuing allocates a number and freezes the document. Voiding keeps the
 * number. Both are confirmed, because neither can be undone and both leave a
 * permanent mark on a numbered sequence somebody's accountant may read.
 */

const STATUS = {
  draft: { label: "Draft — not yet issued", tone: "text-muted-foreground" },
  issued: { label: "Issued", tone: "text-emerald-600" },
  voided: { label: "Voided", tone: "text-destructive" },
};

export default function DocumentDetailPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { authAxios, token, API, isAdmin, isSubAdmin } = useAuth();
  const canIssue = isAdmin || isSubAdmin;

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const res = await api.get(`/documents/${documentId}`);
      setDoc(res.data);
    } catch {
      toast.error("Could not load the document");
    } finally {
      setLoading(false);
    }
  }, [authAxios, documentId]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, confirmText, successText) => {
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const api = authAxios();
      await api.post(`/documents/${documentId}/${path}`);
      toast.success(successText);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft? It has no number, so nothing is left behind."))
      return;
    try {
      const api = authAxios();
      await api.delete(`/documents/${documentId}`);
      toast.success("Draft deleted");
      navigate("/documents");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete the draft");
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-20 bg-muted rounded-xl animate-pulse" />
        <div className="h-72 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="p-8 text-center space-y-2" data-testid="doc-not-found">
        <FileText className="h-9 w-9 mx-auto text-muted-foreground" aria-hidden="true" />
        <p className="text-foreground font-medium">Document not found</p>
      </div>
    );
  }

  const status = STATUS[doc.status] || STATUS.draft;
  // Exports open in a new tab, which cannot carry an auth header — hence the
  // token in the URL, matching the backend's query-token dependency.
  const exportUrl = (format) => `${API}/documents/${documentId}/${format}?token=${token}`;

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
              onClick={() => navigate("/documents")} data-testid="detail-back">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Documents
      </Button>

      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${status.tone}`}
           data-testid="doc-status">
          {status.label}
        </p>
        <h1 className="text-2xl font-bold text-foreground mt-1">{doc.title}</h1>
        <p className="font-mono text-sm text-muted-foreground mt-0.5" data-testid="doc-number">
          {doc.number || "No number until issued"}
        </p>
      </div>

      {doc.status === "voided" && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40
                        bg-destructive/5 p-3" data-testid="doc-voided-notice">
          <Ban className="h-4 w-4 text-destructive mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-xs text-destructive leading-relaxed">
            This document was voided. Its number stays used — sequences with holes in them
            invite questions from anyone auditing the books.
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <a href={exportUrl("pdf")} target="_blank" rel="noopener noreferrer"
           data-testid="doc-pdf">
          <Button variant="outline" className="h-11 gap-1.5">
            <Download className="h-4 w-4" aria-hidden="true" />
            PDF
          </Button>
        </a>
        <a href={exportUrl("excel")} target="_blank" rel="noopener noreferrer"
           data-testid="doc-excel">
          <Button variant="outline" className="h-11 gap-1.5">
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            Excel
          </Button>
        </a>
        {doc.status === "draft" && canIssue && (
          <Button className="h-11 gap-1.5" disabled={busy} data-testid="doc-issue"
                  onClick={() => act("issue",
                    "Issue this document? It will be given a number and can no longer be "
                    + "edited — only voided.",
                    "Document issued")}>
            <Stamp className="h-4 w-4" aria-hidden="true" />
            Issue
          </Button>
        )}
        {doc.status === "issued" && canIssue && (
          <Button variant="outline" className="h-11 gap-1.5 text-destructive" disabled={busy}
                  data-testid="doc-void"
                  onClick={() => act("void",
                    "Void this document? The number stays used and this cannot be undone.",
                    "Document voided")}>
            <Ban className="h-4 w-4" aria-hidden="true" />
            Void
          </Button>
        )}
        {doc.status === "draft" && (
          <Button variant="ghost" className="h-11 gap-1.5 text-destructive"
                  onClick={remove} data-testid="doc-delete">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Delete draft
          </Button>
        )}
      </div>

      {doc.status === "draft" && !canIssue && (
        <p className="text-xs text-muted-foreground flex items-start gap-1.5"
           data-testid="doc-cannot-issue">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          You can draft documents, but putting a number on one is an accounting act — an
          admin issues it.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <CardContent className="p-5 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Supplier</p>
            <p className="font-semibold text-foreground" data-testid="doc-vendor">
              {doc.vendor_snapshot?.legal_name || doc.vendor_snapshot?.name}
            </p>
            {["address", "tax_id", "email", "phone"].map((f) =>
              doc.vendor_snapshot?.[f] ? (
                <p key={f} className="text-xs text-muted-foreground whitespace-pre-line">
                  {doc.vendor_snapshot[f]}
                </p>
              ) : null)}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Buyer</p>
            <p className="font-semibold text-foreground">{doc.org_snapshot?.name}</p>
            {["address", "tax_id", "email"].map((f) =>
              doc.org_snapshot?.[f] ? (
                <p key={f} className="text-xs text-muted-foreground whitespace-pre-line">
                  {doc.org_snapshot[f]}
                </p>
              ) : null)}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">
                  Description
                </th>
                <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Qty</th>
                <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Unit</th>
                <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Amount</th>
              </tr>
            </thead>
            <tbody data-testid="doc-lines">
              {doc.lines.map((line, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="p-3 text-foreground">{line.description}</td>
                  <td className="p-3 text-right text-muted-foreground">{line.quantity}</td>
                  <td className="p-3 text-right font-mono text-muted-foreground">
                    ${line.unit_price}
                  </td>
                  <td className="p-3 text-right font-mono text-foreground">
                    ${line.line_total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-4 space-y-1 text-sm border-t border-border">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span><span className="font-mono">${doc.subtotal}</span>
            </div>
            {Number(doc.tax) > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Tax ({doc.tax_percent}%)</span>
                <span className="font-mono">${doc.tax}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-foreground pt-1">
              <span>Total</span>
              <span className="font-mono" data-testid="doc-total">${doc.total}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {doc.notes && (
        <Card>
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Notes</p>
            <p className="text-sm text-foreground whitespace-pre-line">{doc.notes}</p>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground leading-relaxed" data-testid="doc-footer">
        {doc.footer}
      </p>
    </div>
  );
}
